<template>
  <div
    v-if="value"
    class="modal-bottom"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="title ? titleId : null"
    @click.self="handleBackdrop"
  >
    <section class="modal-bottom__panel" :class="`modal-bottom__panel--${size}`">
      <header class="modal-bottom__header" v-if="title || showClose || $slots.header">
        <slot name="header">
          <h2 v-if="title" :id="titleId" class="modal-bottom__title">{{ title }}</h2>
        </slot>
        <button
          v-if="showClose"
          type="button"
          class="btn modal-bottom__close"
          aria-label="Fechar"
          @click="close"
        >
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="modal-bottom__body">
        <slot></slot>
      </div>

      <footer class="modal-bottom__footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </footer>
    </section>
  </div>
</template>

<script>
let lockedModalCount = 0;

export default {
  name: 'ModalBottom',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'default',
      validator: value => ['default', 'large', 'fullscreen'].includes(value)
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      bodyLocked: false,
      titleId: `modal-bottom-title-${this._uid}`
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(opened) {
        if (opened) {
          this.lockBody();
        } else {
          this.unlockBody();
        }
      }
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.unlockBody();
  },

  methods: {
    close() {
      this.$emit('input', false);
      this.$emit('close');
    },

    handleBackdrop() {
      if (this.closeOnBackdrop) {
        this.close();
      }
    },

    handleKeydown(event) {
      if (this.value && this.closeOnEscape && event.key === 'Escape') {
        this.close();
      }
    },

    lockBody() {
      if (this.bodyLocked) return;
      this.bodyLocked = true;
      lockedModalCount += 1;
      document.body.classList.add('modal-open');
    },

    unlockBody() {
      if (!this.bodyLocked) return;
      this.bodyLocked = false;
      lockedModalCount = Math.max(0, lockedModalCount - 1);

      if (lockedModalCount === 0) {
        document.body.classList.remove('modal-open');
      }
    }
  }
};
</script>

<style scoped>
.modal-bottom {
  position: fixed;
  z-index: 9050;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  padding: 24px;
  overflow: hidden;
  background-color: rgba(11, 14, 11, 0.42);
}

.dark-mode .modal-bottom {
  background-color: rgba(0, 0, 0, 0.64);
}

.modal-bottom__panel {
  width: 100%;
  max-width: 450px;
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--bg-theme-secondary);
  border: 1px solid var(--border-color);
  border-radius: 28px;
  box-shadow: var(--shadow-soft);
}

.modal-bottom__panel--large {
  max-width: 600px;
  height: min(760px, calc(100vh - 48px));
  height: min(760px, calc(100dvh - 48px));
}

.modal-bottom__panel--fullscreen {
  max-width: 760px;
  height: calc(100vh - 48px);
  height: calc(100dvh - 48px);
}

.modal-bottom__header {
  position: relative;
  flex: 0 0 auto;
  min-height: 72px;
  padding: 24px 64px 18px 20px;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-theme-secondary);
}

.modal-bottom__title {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.35;
  font-weight: 700;
  color: var(--text-default);
}

.modal-bottom__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 44px;
  flex: 0 0 44px;
  min-height: 44px;
  padding: 0;
  border-radius: 16px;
  background-color: var(--secondary);
  border: 1px solid var(--border-color);
  color: var(--text-default) !important;
}

.modal-bottom__close:hover,
.modal-bottom__close:focus,
.modal-bottom__close:active {
  background-color: var(--secondary);
  border-color: var(--border-strong);
  color: var(--text-default) !important;
}

.modal-bottom__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.modal-bottom__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 10px;
  padding: 16px 20px calc(16px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-theme-secondary);
  box-shadow: 0 -8px 24px rgba(22, 24, 22, 0.05);
}

@media (max-width: 767px) {
  .modal-bottom {
    align-items: flex-end;
    padding: calc(24px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px))) 0 0;
  }

  .modal-bottom__panel,
  .modal-bottom__panel--large,
  .modal-bottom__panel--fullscreen {
    max-width: none;
    max-height: calc(100vh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)));
    max-height: calc(100dvh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)));
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 28px 28px 0 0;
  }

  .modal-bottom__panel--large {
    height: min(760px, calc(100vh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px))));
    height: min(760px, calc(100dvh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px))));
  }

  .modal-bottom__panel--fullscreen {
    height: calc(100vh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)));
    height: calc(100dvh - 24px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)));
  }

  .modal-bottom__body {
    padding: 20px 16px;
  }

  .modal-bottom__footer {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
